import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API_BASE, errMsg } from "@/lib/api";
import { Wordmark, useMeta, useSite } from "@/components/site/Chrome";
import { useAuth } from "@/App";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toUpperCase() : "";

function TopBar({ children, right }) {
  return (
    <>
      <div className="er-rule" />
      <div className="er-report-top">
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18, minWidth: 0 }}>
          <Wordmark size={17} img={26} to="/dashboard" testid="report-logo" />
          {children}
        </div>
        {right}
      </div>
    </>
  );
}

function ReportQuestionDialog({ note, setNote, onCancel, onSend }) {
  return (
    <div className="er-modal">
      <div className="er-modal-box er-fade">
        <div className="er-rule" />
        <div style={{ padding: 26 }}>
          <p className="er-label">REPORT A PROBLEM</p>
          <p className="er-h2" style={{ marginTop: 12 }}>What looks wrong?</p>
          <textarea data-testid="report-question-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Miskeyed answer, unclear stem, outdated fact…" className="er-input" style={{ marginTop: 14, resize: "vertical" }} />
          <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
            <button onClick={onCancel} className="er-btn-secondary" style={{ flex: 1 }} data-testid="report-question-cancel">Cancel</button>
            <button data-testid="report-question-send" onClick={onSend} className="er-btn" style={{ flex: 1 }}>Send</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreReport({ data, cert, exams, onReview }) {
  const { report, questions } = data;
  const site = useSite();
  const gpass = site?.guarantee?.pass_pct ?? 85;
  const pass = cert?.exam_params?.pass_pct ?? 75;
  const pct = report.overall.pct;
  const missed = questions.filter((q) => !q.is_correct).length;
  const above = pct >= pass;
  const weak = report.domains.find((d) => d.domain === report.weakest_domain);
  const weakWeight = cert?.domains?.find((d) => d.name === report.weakest_domain)?.weight;
  const nExams = cert?.exam_params?.num_paid_exams || 10;
  const label = `${cert?.name || data.cert_slug}${cert?.exam_code ? ` ${cert.exam_code}` : ""}`;
  const order = (cert?.domains || []).map((d) => d.name);
  const domains = [...report.domains].sort((a, b) => (order.indexOf(a.domain) + 1 || 999) - (order.indexOf(b.domain) + 1 || 999));
  return (
    <>
      <section className="er-report-wrap">
        <p className="er-label" data-testid="report-kicker">SCORE REPORT · {label.toUpperCase()} · {data.mode === "free" ? "FREE SAMPLE EXAM" : `PRACTICE EXAM ${data.exam_number}`} · {fmtDate(data.completed_at)}</p>
        <div className="er-score-grid">
          <div className="er-score-cell">
            <p className="er-label" style={{ color: "var(--ink-4)", fontSize: 10 }}>SCORE</p>
            <p className="er-score-big" data-testid="report-score">{Math.round(pct)}<span style={{ fontSize: 22, color: "var(--ink-5)" }}>%</span></p>
            <p style={{ marginTop: 8, fontSize: 13.5, color: "var(--ink-4)" }}>pass mark {pass}%</p>
          </div>
          <div className="er-score-cell">
            <p className="er-label" style={{ color: "var(--ink-4)", fontSize: 10 }}>CORRECT</p>
            <p className="er-score-big" data-testid="report-correct">{report.overall.correct}<span style={{ fontSize: 22, color: "var(--ink-5)" }}>/{report.overall.total}</span></p>
            <p style={{ marginTop: 8, fontSize: 13.5, color: "var(--ink-4)" }}>{missed} missed · every answer explained</p>
          </div>
          <div className="er-score-cell" style={{ display: "flex", flexDirection: "column", justifyContent: "center", background: "var(--ac-tint)" }}>
            <p className="er-label" style={{ color: "var(--ac-deep)", fontSize: 10 }}>RESULT</p>
            <p className="er-display" style={{ marginTop: 8, fontWeight: 900, fontSize: 30, letterSpacing: "-0.02em" }} data-testid="report-result">{above ? "Above pass mark" : "Below pass mark"}</p>
            <p style={{ marginTop: 8, fontSize: 13.5, color: "var(--ink-3)" }}>{data.mode === "free" ? "The full collection scores every domain the same way." : `This exam counts toward your guarantee once it clears ${gpass}%.`}</p>
          </div>
        </div>
      </section>

      <section className="er-report-wrap" style={{ paddingTop: 40 }}>
        <h2 className="er-h2">Score by domain</h2>
        <div className="er-rows" style={{ marginTop: 18 }} data-testid="report-domains">
          {domains.map((d, i) => {
            const isWeak = d.domain === report.weakest_domain && report.domains.length > 1;
            return (
              <div key={d.domain} className={`er-row er-dom-row${isWeak ? " weak" : ""}`} data-testid={`report-domain-${i + 1}`}>
                <p style={{ fontSize: 15, fontWeight: isWeak ? 600 : 400 }}><span className="er-mono" style={{ color: "var(--ink-5)", marginRight: 10, fontWeight: 400 }}>{i + 1}.0</span>{d.domain}{isWeak && <span className="er-label" style={{ color: "var(--ac-deep)", marginLeft: 12, fontSize: 10 }}>WEAKEST</span>}</p>
                <div className="er-bar8"><div className={isWeak ? "dark" : ""} style={{ width: `${d.pct}%` }} /></div>
                <p className="er-mono" style={{ fontSize: 14, textAlign: "right" }}>{d.correct}/{d.total} · {Math.round(d.pct)}%</p>
              </div>
            );
          })}
        </div>
        {weak && report.domains.length > 1 && (
          <div className="er-callout" data-testid="report-weakest-callout">
            <p style={{ fontWeight: 600, fontSize: 15.5 }}>{weak.domain} is costing you the exam.</p>
            <p style={{ marginTop: 7, fontSize: 14.5, lineHeight: 1.6, color: "var(--ink-3)" }}>{weakWeight ? `It carries ${weakWeight}% of the questions and ` : "It is where "}you are {weak.total - weak.correct} down in it. Start there in review mode.</p>
          </div>
        )}
        <div style={{ marginTop: 26, display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button data-testid="report-review-missed-btn" onClick={() => onReview("missed")} className="er-btn" style={{ padding: "13px 24px", fontSize: 14.5 }}>Review {missed} missed question{missed === 1 ? "" : "s"}</button>
          <button data-testid="report-review-all-btn" onClick={() => onReview("all")} className="er-btn-secondary" style={{ padding: "13px 24px", fontSize: 14.5 }}>Review all {questions.length}</button>
          <Link to="/dashboard" className="er-btn-secondary" style={{ padding: "13px 24px", fontSize: 14.5 }} data-testid="report-dashboard-link">{data.mode === "free" ? "My exams" : `Back to my exams`}</Link>
          <a data-testid="report-share-link" href={`${API_BASE}/share-card/${data.id}.svg`} target="_blank" rel="noreferrer" className="er-btn-secondary" style={{ padding: "13px 24px", fontSize: 14.5 }}>Share card</a>
        </div>
      </section>

      {exams && exams.length > 0 && (
        <section className="er-report-wrap" style={{ paddingTop: 40, paddingBottom: 56 }} data-testid="report-guarantee-progress">
          <h2 className="er-h2">Guarantee progress</h2>
          <p style={{ marginTop: 8, fontSize: 14.5, color: "var(--ink-4)" }}>{exams.filter((e) => (e.best_pct || 0) >= gpass).length} of {nExams} exams at {gpass}% or higher. All {nExams} qualify you for the pass guarantee.</p>
          <div className="er-guar-grid">
            {exams.map((e) => (
              <div key={e.exam_number} className={`er-guar-cell${(e.best_pct || 0) >= gpass ? " hit" : ""}`}>
                <p className="er-label" style={{ fontSize: 10, letterSpacing: "0.08em" }}>EXAM {e.exam_number}</p>
                <p className="er-mono" style={{ marginTop: 6, fontSize: 16 }}>{e.best_pct != null ? `${Math.round(e.best_pct)}%` : "—"}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {(!exams || exams.length === 0) && <div style={{ height: 56 }} />}
    </>
  );
}

function ReviewMode({ data, cert, filter, setFilter, onBack, onReport }) {
  const { questions } = data;
  const [idx, setIdx] = useState(0);
  const missed = questions.filter((q) => !q.is_correct);
  const flagged = questions.filter((q) => q.marked);
  const shown = useMemo(() => filter === "all" ? questions : filter === "flagged" ? flagged : missed, [filter, questions, flagged, missed]);
  useEffect(() => { setIdx(0); }, [filter]);
  const q = shown[Math.min(idx, Math.max(0, shown.length - 1))];
  const domIdx = (name) => { const i = (cert?.domains || []).findIndex((d) => d.name === name); return i >= 0 ? `${i + 1}.0` : ""; };
  const label = `${cert?.name || data.cert_slug}${cert?.exam_code ? ` ${cert.exam_code}` : ""}`;
  return (
    <>
      <TopBar right={<button onClick={onBack} className="er-link-btn" style={{ fontSize: 14 }} data-testid="review-back-btn">Back to score report</button>}>
        <span style={{ width: 1, height: 22, background: "var(--hair)" }} className="er-desktop-only" />
        <span className="er-kicker" style={{ fontSize: 11 }}>REVIEW MODE</span>
        <span style={{ fontSize: 14, color: "var(--ink-4)" }} data-testid="review-title">{data.mode === "free" ? "Free sample" : `Exam ${data.exam_number}`} · {Math.round(data.report.overall.pct)}% · {label}</span>
      </TopBar>
      <div className="er-review-grid">
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {[["all", `All ${questions.length}`], ["missed", `Missed ${missed.length}`], ["flagged", `Flagged ${flagged.length}`]].map(([f, l]) => (
              <button key={f} data-testid={`report-filter-${f}`} onClick={() => setFilter(f)} className={`er-tab${filter === f ? " active" : ""}`}>{l}</button>
            ))}
            <span className="er-mono" style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-5)" }} data-testid="review-counter">SHOWING {shown.length ? idx + 1 : 0} OF {shown.length}</span>
          </div>
          {!q ? <p style={{ marginTop: 24, color: "var(--ink-4)" }} data-testid="review-empty">Nothing in this filter.</p> : (
            <div className="er-review-card er-fade" key={q.question_id} data-testid={`report-q-${q.question_number}`}>
              <div className="er-review-head">
                <p className="er-label">QUESTION {q.question_number} · DOMAIN {domIdx(q.domain)} {String(q.domain).toUpperCase()}</p>
                <span className={`er-verdict${q.is_correct ? " ok" : ""}`} data-testid="review-verdict">{q.is_correct ? "CORRECT" : q.user_answer == null ? "UNANSWERED" : "INCORRECT"}</span>
              </div>
              <div style={{ padding: "24px 22px" }}>
                <p style={{ fontSize: 17, lineHeight: 1.65, textWrap: "pretty" }} data-testid="review-stem">{q.stem}</p>
                <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
                  {q.options.map((opt, i) => {
                    const correct = i === q.correct_index, yours = i === q.user_answer && !correct;
                    return (
                      <div key={`${q.question_id}-${i}`} className={`er-ans${correct ? " correct" : yours ? " yours" : ""}`} data-testid={`review-option-${i}`}>
                        <p className="head" style={{ fontWeight: correct ? 600 : 400 }}><span className="l">{"ABCD"[i]}</span><span>{opt}</span>
                          {correct && <span className="tag" style={{ color: "var(--ac-deep)" }}>CORRECT</span>}
                          {yours && <span className="tag" style={{ color: "var(--ink)" }}>YOUR ANSWER</span>}
                        </p>
                        {q.explanations?.[String(i)] && <p className="exp">{q.explanations[String(i)]}</p>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid var(--hair-2)", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", justifyContent: "space-between" }}>
                  <p className="er-mono" style={{ fontSize: 11, color: "var(--ink-5)" }}>SOURCE: {String(cert?.exam_code || label).toUpperCase()} OBJECTIVES · {String(q.domain).toUpperCase()}</p>
                  <button data-testid={`report-question-btn-${q.question_number}`} onClick={() => onReport(q.question_id)} className="er-link-btn" style={{ fontSize: 13, color: "var(--ink-5)", textDecoration: "underline" }}>Report a problem with this question</button>
                </div>
              </div>
            </div>
          )}
          {shown.length > 0 && (
            <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", gap: 12 }}>
              <button data-testid="review-prev-btn" disabled={idx <= 0} onClick={() => setIdx(idx - 1)} className="er-btn-secondary" style={{ fontSize: 14.5 }}>← Previous</button>
              <button data-testid="review-next-btn" disabled={idx >= shown.length - 1} onClick={() => setIdx(idx + 1)} className="er-btn" style={{ fontSize: 14.5 }}>Next →</button>
            </div>
          )}
        </div>
        <aside className="er-miss-aside" data-testid="review-list">
          <p className="er-details-head">{filter === "all" ? "ALL QUESTIONS" : filter === "flagged" ? "FLAGGED QUESTIONS" : "MISSED QUESTIONS"}</p>
          <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {shown.map((m, i) => (
              <button key={m.question_id} onClick={() => setIdx(i)} className={`er-miss-item${i === idx ? " active" : ""}`} data-testid={`review-list-q-${m.question_number}`}>
                <p className="er-mono" style={{ fontSize: 11, color: "var(--ink-5)" }}>Q{m.question_number} · {domIdx(m.domain) || m.domain}</p>
                <p style={{ marginTop: 4, fontSize: 13, lineHeight: 1.45, color: "var(--ink-3)" }}>{m.stem.length > 72 ? `${m.stem.slice(0, 72)}…` : m.stem}</p>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}

export default function Report() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [cert, setCert] = useState(null);
  const [exams, setExams] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [note, setNote] = useState("");
  const view = params.get("view") || "report";
  const filter = params.get("filter") || "missed";
  useMeta(data ? `Score report · ${cert?.name || data.cert_slug} — ExamRay` : "Score report — ExamRay");

  useEffect(() => {
    api.get(`/exam-sessions/${id}/report`).then((r) => {
      setData(r.data);
      api.get(`/certs/${r.data.cert_slug}`).then((c) => setCert(c.data)).catch(() => {});
      if (r.data.mode !== "free") api.get(`/certs/${r.data.cert_slug}/exams`).then((e) => setExams(e.data.exams)).catch(() => setExams([]));
      else setExams([]);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!data) return <div className="er"><div className="er-rule" /><p style={{ padding: 32, color: "var(--ink-5)" }}>Loading report…</p></div>;

  const sendReport = async () => {
    try { await api.post("/questions/report", { question_id: reporting, note }); toast.success("Thanks — sent to the fix queue"); setReporting(null); setNote(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { console.warn(e); } setUser(false); navigate("/"); };
  const go = (v, f) => setParams(f ? { view: v, filter: f } : {});

  return (
    <div className="er" data-testid="report-page">
      {view === "review" ? (
        <ReviewMode data={data} cert={cert} filter={filter} setFilter={(f) => go("review", f)} onBack={() => go("report")} onReport={setReporting} />
      ) : (
        <>
          <TopBar right={<div style={{ display: "flex", gap: 22, fontSize: 14, color: "var(--ink-3)" }}>
            <Link to="/dashboard" style={{ color: "var(--ink-3)" }} data-testid="nav-dashboard">My exams</Link>
            {user && user !== false && <button onClick={logout} className="er-link-btn" style={{ color: "var(--ink-3)" }} data-testid="nav-logout">Sign out</button>}
          </div>} />
          <ScoreReport data={data} cert={cert} exams={exams} onReview={(f) => go("review", f)} />
        </>
      )}
      {reporting && <ReportQuestionDialog note={note} setNote={setNote} onCancel={() => setReporting(null)} onSend={sendReport} />}
    </div>
  );
}
